import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Signal, Wifi, BatteryFull } from 'lucide-react';
import { APPS, SCENE_ALT_TEXT } from './phoneScenes';

const EASE = [0.22, 0.61, 0.36, 1];
const DWELLS = APPS.map((a) => a.dwell);

/*
 * One declarative state machine cycles through the apps. A single setTimeout
 * chain advances the index; it pauses whenever the hero is offscreen
 * (IntersectionObserver) or the tab is hidden (visibilitychange). Reduced motion
 * holds on the AI-receptionist app (the clearest single frame), no timer.
 */
function useAppCycle(containerRef, reduce) {
  const [index, setIndex] = useState(reduce ? APPS.length - 1 : 0);
  const indexRef = useRef(index);
  indexRef.current = index;

  useEffect(() => {
    if (reduce) {
      setIndex(APPS.length - 1);
      return undefined;
    }

    const inView = { current: true };
    const visible = { current: true };
    let timer = null;

    const clear = () => { if (timer) { clearTimeout(timer); timer = null; } };
    const schedule = () => {
      clear();
      if (!inView.current || !visible.current) return;
      timer = setTimeout(() => {
        const next = (indexRef.current + 1) % APPS.length;
        indexRef.current = next;
        setIndex(next);
        schedule();
      }, DWELLS[indexRef.current] || 6500);
    };

    let io = null;
    const el = containerRef.current;
    if (el && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => { inView.current = entries[0]?.isIntersecting ?? true; schedule(); },
        { threshold: 0.2 }
      );
      io.observe(el);
    }
    const onVisibility = () => { visible.current = !document.hidden; schedule(); };
    document.addEventListener('visibilitychange', onVisibility);

    schedule();
    return () => {
      clear();
      if (io) io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [containerRef, reduce]);

  return index;
}

function StatusBar() {
  return (
    <div className="vph-statusbar" aria-hidden="true">
      <span className="vph-time">9:41</span>
      <span className="vph-status-icons">
        <Signal size={13} strokeWidth={2.4} />
        <Wifi size={13} strokeWidth={2.4} />
        <BatteryFull size={16} strokeWidth={2} />
      </span>
    </div>
  );
}

function PhoneDemo() {
  const reduce = useReducedMotion();
  const stageRef = useRef(null);
  const index = useAppCycle(stageRef, reduce);
  const App = APPS[index].Comp;

  return (
    <div className="hero-stage-area" ref={stageRef}>
      {/* Decorative product preview — meaning carried by the sr-only text. */}
      <p className="sr-only">{SCENE_ALT_TEXT}</p>

      <div className="vlh-stage" aria-hidden="true" />

      <div className="vlh-device" aria-hidden="true">
        <div className="vlh-screen">
          <StatusBar />
          <div className="vlh-appwrap">
            <AnimatePresence mode="wait">
              <motion.div
                key={APPS[index].id}
                data-app={APPS[index].id}
                className="vlh-appslot"
                initial={reduce ? false : { opacity: 0, scale: 0.98, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.98, y: -10 }}
                transition={{ duration: reduce ? 0.2 : 0.4, ease: EASE }}
              >
                <App reduce={reduce} containerRef={stageRef} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        {/* Realistic device frame on top; its transparent screen reveals the
            live UI, its opaque island overlaps the status bar. */}
        <img className="vlh-frame-img" src="/phone-frame.avif" alt="" aria-hidden="true" draggable="false" />
      </div>
    </div>
  );
}

export default PhoneDemo;
